// Best-effort text extraction from a PDF, with no PDF library.
//
// The planner attaches reference material to a learner activity so the text
// can travel into the AI Prompt page and on to an assistant. For .txt, .md and
// .json that is a one-line read; PDFs need the content streams inflated and
// their text-showing operators walked.
//
// This deliberately stops short of being a PDF renderer. It handles the case
// that matters — a text PDF (a syllabus, a reading, an article) written by
// Word, InDesign, LaTeX or a browser's "print to PDF" — and reports honestly
// when it cannot read one, rather than attaching mojibake to the lesson plan.
// It cannot read:
//
//   * scans and other image-only PDFs, which contain no text at all;
//   * fonts with a custom CMap where the bytes are glyph ids, not characters;
//   * encrypted PDFs.
//
// In all three cases the caller still records the file — the name and type of
// an attached reading is useful to an assistant even when the words are not
// available — and surfaces the reason to the instructor.

import { inflateFlate } from './inflate';

/** Ratio of readable characters below which extraction is called a failure. */
const READABLE_RATIO_THRESHOLD = 0.6;

/**
 * A TJ array's numbers are kerning adjustments in thousandths of an em.
 * Anything this large is the typesetter opening a word gap rather than
 * tightening a pair, so it becomes a space.
 */
const WORD_GAP_THRESHOLD = 100;

const PDF_ESCAPES = { n: '\n', r: '\r', t: '\t', b: '\b', f: '\f', '(': '(', ')': ')', '\\': '\\' };

/** Bytes → latin1 string, so byte offsets and string offsets stay in step. */
const toLatin1 = (bytes) => {
  let result = '';
  const CHUNK = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    result += String.fromCharCode.apply(null, bytes.subarray(offset, offset + CHUNK));
  }
  return result;
};

/** Read a PDF literal string starting at `(`. Returns [text, indexAfter]. */
const readLiteralString = (source, start) => {
  let index = start + 1;
  let depth = 1;
  let text = '';
  while (index < source.length) {
    const char = source[index];
    if (char === '\\') {
      const next = source[index + 1];
      if (next === undefined) break;
      if (next >= '0' && next <= '7') {
        // Octal escape: up to three digits.
        let digits = '';
        let cursor = index + 1;
        while (digits.length < 3 && source[cursor] >= '0' && source[cursor] <= '7') {
          digits += source[cursor];
          cursor += 1;
        }
        text += String.fromCharCode(parseInt(digits, 8));
        index = cursor;
        continue;
      }
      if (next === '\n' || next === '\r') {
        // Line continuation: the break is not part of the string.
        index += next === '\r' && source[index + 2] === '\n' ? 3 : 2;
        continue;
      }
      text += PDF_ESCAPES[next] !== undefined ? PDF_ESCAPES[next] : next;
      index += 2;
      continue;
    }
    if (char === '(') depth += 1;
    if (char === ')') {
      depth -= 1;
      if (depth === 0) return [text, index + 1];
    }
    text += char;
    index += 1;
  }
  return [text, index];
};

/** Read a PDF hex string starting at `<`. Returns [text, indexAfter]. */
const readHexString = (source, start) => {
  const end = source.indexOf('>', start + 1);
  const body = (end < 0 ? source.slice(start + 1) : source.slice(start + 1, end)).replace(/[^0-9a-f]/gi, '');
  let text = '';
  for (let index = 0; index < body.length; index += 2) {
    const pair = body.slice(index, index + 2).padEnd(2, '0');
    text += String.fromCharCode(parseInt(pair, 16));
  }
  return [text, end < 0 ? source.length : end + 1];
};

/** Skip a `<< … >>` dictionary, which may nest. Returns the index after it. */
const skipDictionary = (source, start) => {
  let index = start + 2;
  let depth = 1;
  while (index < source.length && depth > 0) {
    if (source[index] === '<' && source[index + 1] === '<') {
      depth += 1;
      index += 2;
    } else if (source[index] === '>' && source[index + 1] === '>') {
      depth -= 1;
      index += 2;
    } else if (source[index] === '(') {
      [, index] = readLiteralString(source, index);
    } else {
      index += 1;
    }
  }
  return index;
};

/**
 * Walk one decoded content stream and pull out what it draws as text.
 *
 * Positioning operators (Td, TD, T*, Tm) end a line, which is how a PDF's
 * paragraphs survive the trip: the file has no line breaks of its own, only
 * instructions to move the pen.
 */
export const extractTextFromContentStream = (content) => {
  const lines = [];
  let current = '';
  let pending = [];
  let inArray = false;

  const flushRun = () => {
    if (pending.length) {
      current += pending.join('');
      pending = [];
    }
  };
  const endLine = () => {
    flushRun();
    if (current.trim()) lines.push(current.trim());
    current = '';
  };

  let index = 0;
  while (index < content.length) {
    const char = content[index];

    if (char === '(') {
      const [text, next] = readLiteralString(content, index);
      pending.push(text);
      index = next;
      continue;
    }
    if (char === '<' && content[index + 1] === '<') {
      index = skipDictionary(content, index);
      continue;
    }
    if (char === '<') {
      const [text, next] = readHexString(content, index);
      pending.push(text);
      index = next;
      continue;
    }
    if (char === '[') {
      inArray = true;
      index += 1;
      continue;
    }
    if (char === ']') {
      inArray = false;
      index += 1;
      continue;
    }
    if (char === '-' || char === '.' || (char >= '0' && char <= '9')) {
      let token = '';
      while (index < content.length && /[-+.0-9]/.test(content[index])) {
        token += content[index];
        index += 1;
      }
      // A large negative kern inside a TJ array is a word space.
      if (inArray && Number.parseFloat(token) <= -WORD_GAP_THRESHOLD) pending.push(' ');
      continue;
    }
    if (/[A-Za-z'"*]/.test(char)) {
      let operator = '';
      while (index < content.length && /[A-Za-z0-9'"*]/.test(content[index])) {
        operator += content[index];
        index += 1;
      }
      if (operator === 'Tj' || operator === 'TJ') {
        flushRun();
      } else if (operator === "'" || operator === '"') {
        // Both move to the next line before showing the string.
        const run = pending.join('');
        pending = [];
        endLine();
        current = run;
      } else if (operator === 'Td' || operator === 'TD' || operator === 'T*' || operator === 'Tm') {
        endLine();
      } else if (operator === 'ET' || operator === 'BT') {
        endLine();
      } else {
        // Any other operator ends the run without ending the line — the
        // strings it consumed are not text (font names, XObject names).
        pending = [];
      }
      continue;
    }
    index += 1;
  }

  endLine();
  return lines.join('\n');
};

/** How much of the extracted text is characters a person could read. */
export const readableRatio = (text) => {
  if (!text) return 0;
  const readable = text.match(/[\p{L}\p{N}\p{P}\p{Zs}\n]/gu);
  return (readable ? readable.length : 0) / text.length;
};

/**
 * Pull the text out of a PDF.
 *
 * @param {ArrayBuffer|Uint8Array} buffer
 * @returns {Promise<{ text: string, note: string }>} `note` is empty on a
 *   clean read and otherwise explains what could not be read.
 */
export const extractPdfText = async (buffer) => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const source = toLatin1(bytes);

  if (!source.startsWith('%PDF-')) {
    return { text: '', note: 'That file does not look like a PDF.' };
  }
  if (/\/Encrypt\b/.test(source)) {
    return { text: '', note: 'This PDF is encrypted, so its text could not be read.' };
  }

  const pieces = [];
  let failedStreams = 0;
  let cursor = 0;

  while (cursor < source.length) {
    const streamStart = source.indexOf('stream', cursor);
    if (streamStart < 0) break;

    const dictionary = source.slice(Math.max(0, streamStart - 600), streamStart);
    // Data starts after the EOL that must follow the `stream` keyword.
    let dataStart = streamStart + 'stream'.length;
    if (source[dataStart] === '\r') dataStart += 1;
    if (source[dataStart] === '\n') dataStart += 1;

    const streamEnd = source.indexOf('endstream', dataStart);
    if (streamEnd < 0) break;
    cursor = streamEnd + 'endstream'.length;

    // The spec puts an EOL between the data and `endstream`, and it is not
    // part of the data. Leaving it on is trailing garbage as far as the
    // inflater is concerned, which fails the whole stream.
    let dataEnd = streamEnd;
    if (bytes[dataEnd - 1] === 0x0a) dataEnd -= 1;
    if (bytes[dataEnd - 1] === 0x0d) dataEnd -= 1;

    // Images, fonts and metadata are streams too, and none of them hold page text.
    if (/\/Subtype\s*\/(Image|Type1C|CIDFontType0C|TrueType)|\/Type\s*\/(XObject|Font|Metadata)\b/.test(dictionary)
      && !/\/Subtype\s*\/Form\b/.test(dictionary)) {
      continue;
    }

    const raw = bytes.subarray(dataStart, dataEnd);
    if (!raw.length) continue;

    let decoded;
    if (/\/Filter\s*(\[[^\]]*)?\/FlateDecode/.test(dictionary)) {
      try {
        decoded = toLatin1(await inflateFlate(raw));
      } catch (error) {
        failedStreams += 1;
        continue;
      }
    } else if (/\/Filter\b/.test(dictionary)) {
      // Some other filter (LZW, ASCII85, DCT…) — not worth reimplementing.
      failedStreams += 1;
      continue;
    } else {
      decoded = source.slice(dataStart, dataEnd);
    }

    // A content stream is the only kind that draws text.
    if (!/\b(Tj|TJ|BT)\b/.test(decoded)) continue;

    const text = extractTextFromContentStream(decoded);
    if (text.trim()) pieces.push(text);
  }

  const text = pieces.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();

  if (!text) {
    return {
      text: '',
      note: failedStreams
        ? 'No readable text was found — this PDF uses a compression or font format this reader does not support.'
        : 'No text layer was found — this PDF is probably a scan. The file is still attached by name.'
    };
  }
  if (readableRatio(text) < READABLE_RATIO_THRESHOLD) {
    return {
      text: '',
      note: 'The text in this PDF is stored with a custom font encoding that could not be decoded.'
    };
  }
  return {
    text,
    note: failedStreams ? 'Some parts of this PDF could not be read; the rest was extracted.' : ''
  };
};

export const __testing = { readLiteralString, readHexString, skipDictionary, toLatin1 };
